import { Box, Button, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ALL_OPTION_ID,
  OTHER_OPTION_ID,
  defaultSelections,
  parseTemplate,
  renderRewrittenQuestion,
} from "@/features/chat/lib/clarificationRewrite";
import type {
  ClarificationAnswerItem,
  ClarificationRewrite,
  ClarificationSlot,
} from "@/features/chat/model/chatTypes";
import { ClarificationSlotDropdown } from "@/features/chat/ui/ClarificationSlotDropdown";

export type ClarificationRewritePanelProps = {
  rewrite: ClarificationRewrite;
  disabled?: boolean;
  onSubmit: (answers: ClarificationAnswerItem[], rewrittenQuestion: string) => void;
  onRewrite: (sourceQuestion: string) => void;
};

function buildAnswer(
  slot: ClarificationSlot,
  selectedOptionIds: string[],
  customText: string,
): ClarificationAnswerItem {
  if (selectedOptionIds.includes(OTHER_OPTION_ID)) {
    return {
      question_id: slot.id,
      selected_option_id: OTHER_OPTION_ID,
      custom_text: customText.trim(),
    };
  }
  // "All of them" is expanded client-side into the concrete ids it stands for, so the
  // primary pick stays a real option id the backend already knows.
  const optionIds = selectedOptionIds.includes(ALL_OPTION_ID)
    ? slot.options
        .filter((option) => option.id !== ALL_OPTION_ID && option.id !== OTHER_OPTION_ID)
        .map((option) => option.id)
    : selectedOptionIds;
  return {
    question_id: slot.id,
    selected_option_id: optionIds[0] ?? slot.default_option_id,
    selected_option_ids: optionIds.length > 1 ? optionIds : undefined,
    custom_text: null,
  };
}

export function ClarificationRewritePanel({
  rewrite,
  disabled = false,
  onSubmit,
  onRewrite,
}: ClarificationRewritePanelProps) {
  const { t } = useTranslation("chat");
  // The top pick is pre-selected, so the sentence reads as complete on arrival and
  // Ask is enabled immediately — answering is a correction, not a mandatory step.
  const [selections, setSelections] = useState<Record<string, string[]>>(() =>
    defaultSelections(rewrite),
  );
  const [customTexts, setCustomTexts] = useState<Record<string, string>>({});

  const slotsById = useMemo(
    () => new Map(rewrite.slots.map((slot) => [slot.id, slot])),
    [rewrite.slots],
  );
  const segments = useMemo(() => parseTemplate(rewrite.template), [rewrite.template]);

  const hasEmptyCustomText = rewrite.slots.some(
    (slot) =>
      (selections[slot.id] ?? []).includes(OTHER_OPTION_ID) &&
      (customTexts[slot.id] ?? "").trim().length === 0,
  );
  const canSubmit = !disabled && !hasEmptyCustomText;

  const handleAsk = () => {
    if (!canSubmit) {
      return;
    }
    const answers = rewrite.slots.map((slot) =>
      buildAnswer(slot, selections[slot.id] ?? [slot.default_option_id], customTexts[slot.id] ?? ""),
    );
    onSubmit(answers, renderRewrittenQuestion(rewrite, selections, customTexts));
  };

  return (
    <Box
      sx={{
        border: "1px solid var(--theme-border)",
        borderRadius: 2,
        p: 2,
        bgcolor: "var(--theme-surface)",
      }}
      data-testid="clarification-rewrite-panel"
    >
      <Stack spacing={2}>
        <Typography
          variant="body1"
          component="p"
          sx={{ color: "var(--theme-text-primary)", lineHeight: 2 }}
        >
          {segments.map((segment, index) => {
            if (segment.kind === "text") {
              // Preserve the user's own spacing between spans.
              return (
                <Box component="span" key={`text-${index}`} sx={{ whiteSpace: "pre-wrap" }}>
                  {segment.text}
                </Box>
              );
            }
            const slot = slotsById.get(segment.slotId);
            if (!slot) {
              return null;
            }
            return (
              <ClarificationSlotDropdown
                key={slot.id}
                slot={slot}
                selectedOptionIds={selections[slot.id] ?? [slot.default_option_id]}
                customText={customTexts[slot.id] ?? ""}
                disabled={disabled}
                onSelectionChange={(optionIds) =>
                  setSelections((current) => ({ ...current, [slot.id]: optionIds }))
                }
                onCustomTextChange={(value) =>
                  setCustomTexts((current) => ({ ...current, [slot.id]: value }))
                }
              />
            );
          })}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            type="button"
            variant="text"
            disabled={disabled}
            onClick={() => onRewrite(rewrite.source_question)}
            sx={{ textTransform: "none", color: "var(--theme-text-secondary)" }}
          >
            {t("clarification.rewrite")}
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={!canSubmit}
            onClick={handleAsk}
            sx={{
              textTransform: "none",
              backgroundColor: "var(--theme-primary)",
              color: "var(--theme-text-on-primary)",
              "&:hover": { backgroundColor: "var(--theme-hover-primary)" },
            }}
          >
            {t("clarification.ask")}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
